import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

// Zod schema para los search params de /clients. Sirve doble:
// 1) validateSearch en el route file
// 2) tipar el resultado de useSearch
//
// Defaults explícitos: tab=todas, vigencia=todas, completitud=todas,
// sort=nombre_asc, page=1.
export const clientsSearchSchema = z.object({
  search: z.string().optional(),
  tab: z
    .enum([
      'todas',
      'contabilidad',
      'oficina_virtual',
      'renta',
      'remuneraciones',
      'otros',
    ])
    // Tolera valores legacy de URLs guardadas o saved views: cuando llega
    // 'crea_pyme'/'regularizacion'/'termino_giro'/'habilitacion_pyme' (que
    // ya no están en el grid), caemos a 'todas' sin romper la navegación.
    .catch('todas')
    .default('todas'),
  vigencia: z.enum(['todas', 'por_vencer', 'vencidas']).default('todas'),
  completitud: z.enum(['todas', 'baja', 'media', 'alta']).default('todas'),
  sort: z
    .enum([
      'nombre_asc',
      'completitud_asc',
      'completitud_desc',
      'monto_desc',
      'alta_desc',
      'vencimiento_asc',
    ])
    .default('nombre_asc'),
  page: z.coerce.number().int().min(1).default(1),
  cliente: z.string().uuid().optional(),
  // Tab activa del drawer detalle. Solo aplica cuando `cliente` está seteado.
  // Item #1: 'credenciales' agregado para deeplink al tab de credenciales.
  clienteTab: z
    .enum([
      'resumen',
      'servicios',
      'socios',
      'contactos',
      'bancarios',
      'tareas',
      'onboarding',
      'contratos',
      'informes',
      'credenciales',
      'portal',
      'bitacora',
      'obligaciones',
      'overview',
    ])
    .optional(),
  // ?nuevo=1 abre el dialog de creación.
  nuevo: z.coerce.boolean().optional(),
  // ?editar=<uuid> abre el drawer de edición con ese cliente.
  editar: z.string().uuid().optional(),
  // ?reasignar=<uuid> abre el dialog de reasignación de ejecutivo.
  reasignar: z.string().uuid().optional(),
  quick: z
    .enum([
      'mis_clientes',
      'sin_bienvenida',
      'sin_claves',
      'sin_ejecutivo',
      'inactivos_90d',
      'vencen_este_mes',
      'operativo_por_firmar',
      'sin_contrato',
      'sin_contabilidad',
    ])
    .optional(),
  // Saved views multi-módulo — id de la vista activa.
  viewId: z.string().uuid().optional(),
  // Filter por ejecutivo asignado. Soporta UUID (1 ejecutivo) o el
  // literal 'sin' (clientes sin asignar). El backend ya soporta
  // `assignedExecutiveId` en repository.listClients.
  ejecutivoId: z.union([z.string().uuid(), z.literal('sin')]).optional(),
  // Suma clientes con `estado='baja'` al universo del listado. Default
  // ocultos. Activa el chip auxiliar "Mostrar dados de baja" en la
  // barra de filtros rápidos.
  incluirBajas: z.coerce.boolean().optional(),
});

export type ClientsSearchParams = z.infer<typeof clientsSearchSchema>;

// Hook centralizado para leer/escribir el URL state de la página /clients.
// Todas las secciones (toolbar, tabs, quick filters, KPI drill-down) lo usan.
export function useClientsTableState() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/clients/' }) as ClientsSearchParams;

  const update = useCallback(
    (patch: Partial<ClientsSearchParams>) => {
      const next = { ...search, ...patch };
      const filterChanged =
        'search' in patch ||
        'tab' in patch ||
        'vigencia' in patch ||
        'completitud' in patch ||
        'sort' in patch ||
        'quick' in patch;
      if (filterChanged && !('page' in patch)) next.page = 1;
      void navigate({
        to: '/clients',
        // Cast: TanStack Router infiere el tipo del search reducer como
        // exact-required; nuestro shape (con defaults Zod) es estructuralmente
        // equivalente. El cast evita conflicto de variancia.
        search: next as never,
      });
    },
    [navigate, search],
  );

  const reset = useCallback(() => {
    void navigate({
      to: '/clients',
      search: {
        tab: 'todas',
        vigencia: 'todas',
        completitud: 'todas',
        sort: 'nombre_asc',
        page: 1,
      } as never,
    });
  }, [navigate]);

  return { search, update, reset };
}
